import { Dispatch, SetStateAction, useState } from "react";

import useEventCallback from "@/mk2/hooks/useEventCallback";

interface InitialValues {
    [key: string]: any;
}

type ReturnValue = [
    boolean,
    (event: React.MouseEvent<HTMLElement>) => void,
    Dispatch<SetStateAction<boolean>>,
];

/**
 * USAGE: when you have component which displays some form with initial values based on toggle buttons
 * and the form should be closed after initial values has changed.
 *
 * https://reactjs.org/docs/hooks-faq.html#how-do-i-implement-getderivedstatefromprops
 * @param initialValues
 */
function useHiddenForm(initialValues: InitialValues): ReturnValue {
    const [showForm, setShowForm] = useState(false);
    const [prevInitialValues, setPrevInitialValues] = useState(initialValues);

    const initialValuesChanged = prevInitialValues !== initialValues;
    if (initialValuesChanged) {
        setPrevInitialValues(initialValues);
    }
    if (initialValuesChanged && showForm) {
        // Close form after update
        setShowForm(false);
    }

    const onToggleClick = useEventCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            event?.preventDefault();
            setShowForm((s) => !s);
        },
        [setShowForm],
    );

    return [showForm, onToggleClick, setShowForm];
}

export default useHiddenForm;
